
import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';
import { getCourierByPhone } from '@/utils/supabase/courierQueries';
import { supabase } from '@/integrations/supabase/client';

export function useTariffInfo() {
  const [showTariffSelection, setShowTariffSelection] = useState(false);
  const [showFullscreenTariff, setShowFullscreenTariff] = useState(false);
  const [currentTariff, setCurrentTariff] = useState<string | null>(null);
  const [amoCustomerCode, setAmoCustomerCode] = useState<string | null>(null);
  const [amoDealCode, setAmoDealCode] = useState<string | null>(null);
  const [clientNumber, setClientNumber] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [courierData, setCourierData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchCourierTariff = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        console.log("Загрузка данных курьера для пользователя:", user);
        
        let data;
        let error;
        
        // Проверка для тестового пользователя
        if (user.phone === '+71234567890') {
          const testId = '6ab29258-5ca8-43b2-992a-88a0ea513e0c';
          console.log("Это тестовый пользователь, запрашиваем по ID:", testId);
          
          try {
            // Используем select('*') вместо maybeSingle() для предотвращения ошибки 406
            const { data: courierData, error: courierError } = await supabase
              .from('couriers')
              .select('*')
              .eq('id', testId)
              .limit(1);
            
            if (courierError) {
              throw courierError;
            }
            
            // Проверяем, есть ли данные, и берем первый элемент массива
            data = courierData && courierData.length > 0 ? courierData[0] : null;
            console.log("Получены данные для тестового пользователя:", data);
          } catch (e) {
            // Если произошла ошибка, используем фиктивные данные для тестирования
            data = {
              id: testId,
              name: 'Тестовый Курьер',
              email: 'test@example.com',
              phone: user.phone,
              status: 'active',
              role: 'courier',
              tariff: 'Стандартный тариф',
              amo_contact_id: '12345',
              amo_deal_id: '67890',
              deal_number: 'TEST-001'
            };
            console.log("Использованы тестовые данные для тестового пользователя");
          }
        } else if (user.phone) {
          // Если у пользователя есть телефон, ищем по телефону
          console.log("Поиск курьера по телефону:", user.phone);
          const result = await getCourierByPhone(user.phone);
          data = result.data;
          error = result.error;
          console.log("Результат поиска по телефону:", {data, error});
        } 
        
        // Если не найдено по телефону или телефона нет, пробуем еще способы
        if ((!data || error) && user.id) {
          // В этом случае можем попробовать создать новую запись курьера
          console.log("Курьер не найден, создаем новую запись курьера");
          
          // Генерируем уникальный ID для курьера (не связанный с user.id)
          const courierId = 'courier-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
          
          // Создаем нового курьера с уникальным ID
          const { data: newCourier, error: createError } = await supabase
            .from('couriers')
            .upsert({
              id: courierId,
              name: user.email ? user.email.split('@')[0] : 'Новый пользователь',
              phone: user.phone || null,
              email: user.email || null,
              status: 'active',
              role: 'courier'
            })
            .select();
            
          if (createError) {
            console.error("Ошибка при создании записи курьера:", createError);
            setError("Не удалось создать профиль курьера");
          } else if (newCourier && newCourier.length > 0) {
            console.log("Создан новый профиль курьера:", newCourier[0]);
            data = newCourier[0];
          }
        }
        
        if (data) {
          console.log("Полученные данные курьера для тарифа:", data);
          setCourierData(data);
          setCurrentTariff(data?.tariff || 'Не указан');
          
          const amoContact = data?.amo_contact_id || null;
          const amoDeal = data?.amo_deal_id || null;
          const dealNumber = data?.deal_number || null;

          console.log("AMO данные курьера:", {
            amo_contact_id: amoContact,
            amo_deal_id: amoDeal,
            deal_number: dealNumber
          });

          setAmoCustomerCode(amoContact);
          setAmoDealCode(amoDeal);
          setClientNumber(dealNumber);
        } else {
          // Если данных нет, используем значения по умолчанию
          console.log("Данные курьера не найдены, используем значения по умолчанию");
          setCurrentTariff('Не выбран');
          
          // Устанавливаем фиктивные данные для тестирования
          if (process.env.NODE_ENV === 'development') {
            setAmoCustomerCode('dev_contact');
            setAmoDealCode('dev_deal');
            setClientNumber('DEV-001');
          }
        }
      } catch (error: any) {
        console.error('Error fetching courier tariff:', error);
        setError(error.message || "Ошибка при загрузке данных");
        toast.error('Не удалось загрузить информацию о тарифе');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourierTariff();
  }, [user]);

  useEffect(() => {
    console.log("AMO коды обновлены:", { amoCustomerCode, amoDealCode, clientNumber });
    console.log("Текущий пользователь:", user);
  }, [amoCustomerCode, amoDealCode, clientNumber, user]);

  const handleOpenFullscreenTariff = () => {
    console.log("Открываем выбор тарифа. Данные курьера:", courierData);
    console.log("AMO коды:", { amoCustomerCode, amoDealCode, clientNumber });
    setShowFullscreenTariff(true);
  };

  const handleOpenTariffDialog = () => {
    console.log("Открываем диалог выбора тарифа");
    setShowTariffSelection(true);
  };

  return {
    isLoading,
    error,
    courierData,
    currentTariff,
    amoCustomerCode,
    amoDealCode,
    clientNumber,
    showTariffSelection,
    setShowTariffSelection,
    showFullscreenTariff,
    setShowFullscreenTariff,
    handleOpenFullscreenTariff,
    handleOpenTariffDialog
  };
}
